import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4 md:py-5">
            {/* Logo et titre - lien vers l'accueil */}
            <a
              href="/"
              className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity"
            >
              <img
                src="/icon.png"
                alt="BamakoFuel Logo"
                className="h-10 sm:h-14 md:h-16 w-auto max-w-[120px] sm:max-w-[160px] md:max-w-[200px] object-contain"
              />
              <div>
                <h1 className="text-lg sm:text-2xl font-black text-blue-600 tracking-tight">bkofuel</h1>
                <p className="hidden sm:block text-sm text-gray-600">Carburant en temps réel</p>
              </div>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Politique de Confidentialité
          </h1>

          <div className="prose prose-blue max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-600 mb-4">
                <strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR')}
              </p>

              <p className="text-gray-700 leading-relaxed mb-4">
                BamakoFuel ("nous", "notre" ou "nos") s'engage à protéger et respecter votre vie privée.
                Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et
                protégeons vos informations personnelles lorsque vous utilisez notre application mobile et
                notre site web BamakoFuel, accessible à l'adresse bkofuel.com.
              </p>

              <p className="text-gray-700 leading-relaxed">
                BamakoFuel est une plateforme communautaire qui permet aux utilisateurs de partager des
                informations en temps réel sur la disponibilité de carburant dans les stations-service
                de Bamako, Mali.
              </p>
            </section>

            {/* Informations collectées */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Informations que nous collectons
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                1.1 Données de localisation
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Localisation précise :</strong> Avec votre autorisation, nous collectons votre position GPS
                  pour vous montrer les stations-service les plus proches de vous.
                </li>
                <li>
                  <strong>Localisation approximative :</strong> Pour améliorer l'expérience utilisateur et
                  personnaliser le contenu selon votre région.
                </li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                1.2 Signalements et contributions
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Les signalements de disponibilité de carburant que vous soumettez</li>
                <li>Vos votes (confirmations ou contestations) sur les signalements d'autres utilisateurs</li>
                <li>Les commentaires que vous ajoutez aux signalements</li>
                <li>Les suggestions de nouvelles stations-service</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                1.3 Données techniques
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Informations sur votre appareil (type, modèle, système d'exploitation)</li>
                <li>Adresse IP</li>
                <li>Identifiant unique de l'appareil</li>
                <li>Données d'utilisation de l'application (pages visitées, fonctionnalités utilisées)</li>
                <li>Journaux d'erreurs et de performance</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                1.4 Données de contact (optionnel)
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Adresse e-mail si vous nous contactez ou vous inscrivez aux notifications</li>
                <li>Informations fournies dans vos messages de contact</li>
              </ul>
            </section>

            {/* Utilisation des données */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Comment nous utilisons vos informations
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.1 Fonctionnalités principales
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Afficher les stations-service proches de votre localisation</li>
                <li>Traiter et publier vos signalements de disponibilité de carburant</li>
                <li>Permettre la validation communautaire des informations</li>
                <li>Fournir des informations actualisées sur l'état des stocks de carburant</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.2 Amélioration du service
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Analyser l'utilisation de l'application pour identifier les améliorations</li>
                <li>Détecter et corriger les bugs et problèmes techniques</li>
                <li>Optimiser les performances de l'application</li>
                <li>Développer de nouvelles fonctionnalités</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                2.3 Communication
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Envoyer des notifications push sur la disponibilité de carburant (avec votre autorisation)</li>
                <li>Répondre à vos questions et demandes de support</li>
                <li>Vous informer des mises à jour importantes de l'application</li>
              </ul>
            </section>

            {/* Partage des données */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Partage de vos informations
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                3.1 Données anonymisées pour l'analytique
              </h3>
              <p className="text-gray-700 mb-4">
                Nous utilisons <strong>Datadog</strong> pour l'analyse des performances et l'amélioration
                de notre service. Les données partagées avec Datadog sont anonymisées et incluent :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Métriques d'utilisation de l'application (nombre de signalements, pages visitées)</li>
                <li>Données de performance technique (temps de chargement, erreurs)</li>
                <li>Statistiques d'utilisation géographique (sans identification personnelle)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                3.2 Partage public
              </h3>
              <p className="text-gray-700 mb-4">
                Les signalements que vous soumettez sont rendus publics pour tous les utilisateurs
                de BamakoFuel, mais sans aucune information permettant de vous identifier personnellement.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                3.3 Cas exceptionnels
              </h3>
              <p className="text-gray-700 mb-4">
                Nous pourrions être amenés à divulguer vos informations dans les cas suivants :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Si la loi l'exige ou en réponse à une demande légale valide</li>
                <li>Pour protéger nos droits, notre propriété ou notre sécurité</li>
                <li>En cas de fusion, acquisition ou vente d'actifs</li>
              </ul>
            </section>

            {/* Sécurité */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Sécurité de vos données
              </h2>

              <p className="text-gray-700 mb-4">
                Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos informations
                personnelles contre l'accès non autorisé, l'altération, la divulgation ou la destruction :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Chiffrement des données en transit et au repos</li>
                <li>Accès restreint aux données personnelles</li>
                <li>Surveillance continue de la sécurité</li>
                <li>Mise à jour régulière des systèmes de sécurité</li>
                <li>Stockage sécurisé dans une base de données PostgreSQL protégée</li>
              </ul>
            </section>

            {/* Conservation des données */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Conservation des données
              </h2>

              <p className="text-gray-700 mb-4">
                Nous conservons vos informations personnelles uniquement pendant la durée nécessaire
                aux finalités décrites dans cette politique :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Signalements :</strong> Conservés pendant 6 mois pour maintenir l'historique
                  utile aux utilisateurs
                </li>
                <li>
                  <strong>Données de localisation :</strong> Non stockées de manière permanente,
                  utilisées uniquement pour la session en cours
                </li>
                <li>
                  <strong>Données techniques :</strong> Conservées pendant 12 mois à des fins d'amélioration
                  du service
                </li>
                <li>
                  <strong>Données de contact :</strong> Conservées tant que vous n'en demandez pas la suppression
                </li>
              </ul>
            </section>

            {/* Droits des utilisateurs */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Vos droits
              </h2>

              <p className="text-gray-700 mb-4">
                Vous disposez des droits suivants concernant vos données personnelles :
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                6.1 Droit d'accès et de rectification
              </h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez demander l'accès à vos données personnelles et leur rectification
                si elles sont inexactes ou incomplètes.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                6.2 Droit de suppression
              </h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez demander la suppression de vos données personnelles. Notez que cela
                peut limiter votre capacité à utiliser certaines fonctionnalités de l'application.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                6.3 Droit d'opposition
              </h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez vous opposer au traitement de vos données personnelles à des fins
                d'amélioration du service ou d'analyse.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                6.4 Gestion des autorisations
              </h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Localisation :</strong> Vous pouvez désactiver l'accès à votre localisation
                  dans les paramètres de votre appareil
                </li>
                <li>
                  <strong>Notifications :</strong> Vous pouvez désactiver les notifications push
                  dans les paramètres de l'application ou de votre appareil
                </li>
                <li>
                  <strong>Caméra :</strong> Vous pouvez révoquer l'autorisation d'accès à l'appareil photo
                  dans les paramètres de votre appareil
                </li>
              </ul>
            </section>

            {/* Autorisations de l'application */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Autorisations de l'application
              </h2>

              <p className="text-gray-700 mb-4">
                Notre application demande les autorisations suivantes :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Localisation (fine/approximative) :</strong> Pour identifier les stations-service
                  proches de vous et améliorer la pertinence des informations
                </li>
                <li>
                  <strong>Caméra :</strong> Pour permettre la prise de photos lors des signalements
                  de stations-service (optionnel)
                </li>
                <li>
                  <strong>Notifications :</strong> Pour vous alerter sur la disponibilité de carburant
                  et les mises à jour importantes
                </li>
                <li>
                  <strong>Internet :</strong> Pour synchroniser les données et permettre le fonctionnement
                  de l'application
                </li>
              </ul>

              <p className="text-gray-700">
                Toutes ces autorisations peuvent être révoquées à tout moment dans les paramètres
                de votre appareil.
              </p>
            </section>

            {/* Services tiers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Services tiers
              </h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                8.1 Datadog RUM (Real User Monitoring)
              </h3>
              <p className="text-gray-700 mb-4">
                Nous utilisons Datadog pour surveiller les performances de notre application et
                améliorer l'expérience utilisateur. Datadog collecte des données anonymisées sur :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Les performances de l'application (temps de chargement, erreurs)</li>
                <li>L'utilisation des fonctionnalités</li>
                <li>Les métriques techniques</li>
              </ul>

              <p className="text-gray-700 mb-4">
                Pour plus d'informations sur les pratiques de confidentialité de Datadog,
                consultez leur politique de confidentialité sur
                <a href="https://www.datadoghq.com/legal/privacy/" className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
                  datadoghq.com/legal/privacy/
                </a>
              </p>
            </section>

            {/* Modifications de la politique */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Modifications de cette politique
              </h2>

              <p className="text-gray-700 mb-4">
                Nous pouvons mettre à jour cette politique de confidentialité périodiquement.
                Toute modification sera publiée sur cette page avec une date de mise à jour révisée.
                Nous vous encourageons à consulter régulièrement cette politique pour rester informé
                de nos pratiques en matière de protection des données.
              </p>

              <p className="text-gray-700">
                Les modifications importantes seront communiquées par une notification dans l'application
                ou par e-mail si vous avez fourni votre adresse e-mail.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Nous contacter
              </h2>

              <p className="text-gray-700 mb-4">
                Si vous avez des questions concernant cette politique de confidentialité ou
                nos pratiques de traitement des données, ou si vous souhaitez exercer vos droits,
                n'hésitez pas à nous contacter :
              </p>

              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Équipe BamakoFuel</h3>
                <p className="text-gray-700 mb-2">
                  <strong>E-mail :</strong>
                  <a href="mailto:admin@bkofuel.com" className="text-blue-600 hover:text-blue-800 underline ml-1">
                    admin@bkofuel.com
                  </a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Site web :</strong>
                  <a href="https://bkofuel.com" className="text-blue-600 hover:text-blue-800 underline ml-1" target="_blank" rel="noopener noreferrer">
                    bkofuel.com
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>Objet :</strong> Question relative à la politique de confidentialité
                </p>
              </div>
            </section>

            {/* Conformité légale */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Conformité légale
              </h2>

              <p className="text-gray-700 mb-4">
                Cette politique de confidentialité est conçue pour être conforme aux exigences
                applicables en matière de protection des données, notamment :
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Le Règlement général sur la protection des données (RGPD) de l'Union européenne</li>
                <li>Les lois maliennes applicables en matière de protection des données</li>
                <li>Les exigences des plateformes de distribution d'applications mobiles</li>
              </ul>

              <p className="text-gray-700">
                BamakoFuel s'engage à traiter vos données personnelles de manière licite,
                équitable et transparente, et à respecter vos droits en matière de protection des données.
              </p>
            </section>
          </div>

          {/* Retour à l'accueil */}
          <div className="mt-12 pt-8 border-t text-center">
            <a
              href="/"
              className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors shadow-lg"
            >
              <span>←</span>
              <span>Retour à BamakoFuel</span>
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p className="mb-2">
              © 2024 BamakoFuel - Plateforme communautaire de signalement de carburant
            </p>
            <div className="flex justify-center space-x-6">
              <a href="/" className="text-blue-600 hover:text-blue-800 transition-colors">
                Accueil
              </a>
              <a href="/vie-privee" className="text-blue-600 hover:text-blue-800 transition-colors">
                Politique de Confidentialité
              </a>
              <a
                href="mailto:admin@bkofuel.com"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;